import { useState, useEffect } from "react";
import BottomNav from "../components/BottomNav";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { objectsApi } from "../services/api";

const T = {
  night:"#0A1F3D", royal:"#1263B0", cyan:"#1CBCD3", gold:"#C9922A",
  goldL:"#FBF4E8", cyanL:"#E0F7FA", white:"#fff",
  bg:"#F7F9FC", border:"#E2E8F0", sub:"#64748B"
};

function formatDate(dateStr, lang) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString(lang === "en" ? "en-US" : "fr-FR", {
      day: "2-digit", month: "long", year: "numeric",
    });
  } catch { return dateStr; }
}

export default function Matchings() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith("en") ? "en" : "fr";
  const nav = useNavigate();
  const [matchings, setMatchings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    objectsApi.getMyMatchings()
      .then(r => setMatchings(r.data?.data || []))
      .catch(() => setMatchings([]))
      .finally(() => setLoading(false));
  }, []);

  const openMatching = (m) => {
    sessionStorage.setItem("matching_" + m.id, JSON.stringify(m));
    nav(`/matching/${m.id}`);
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:"'DM Sans',sans-serif" }}>
      <div style={{ background:`linear-gradient(135deg,${T.night},${T.royal})`,
        padding:"20px 24px", display:"flex", alignItems:"center", gap:"16px" }}>
        <button onClick={() => nav("/dashboard")}
          style={{ background:"transparent", border:"none", color:T.white,
            fontSize:"20px", cursor:"pointer" }}>←</button>
        <h1 style={{ color:T.white, fontSize:"18px", fontWeight:700, margin:0 }}>
          {t("matchings.title")}
        </h1>
      </div>

      <div style={{ maxWidth:"760px", margin:"0 auto", padding:"24px 24px 80px" }}>
        {loading ? (
          <div style={{ textAlign:"center", padding:"60px", color:T.sub }}>
            {t("common.loading")}
          </div>
        ) : matchings.length === 0 ? (
          <div style={{ textAlign:"center", padding:"60px" }}>
            <div style={{ fontSize:"48px", marginBottom:"16px" }}>🎯</div>
            <p style={{ color:T.night, fontWeight:700, fontSize:"15px", marginBottom:"6px" }}>
              {t("matchings.empty_title")}
            </p>
            <p style={{ color:T.sub, fontSize:"13px" }}>
              {t("matchings.empty_desc")}
            </p>
          </div>
        ) : (
          <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
            {matchings.map((m) => (
              <MatchingCard key={m.id} m={m} t={t} lang={lang} onOpen={() => openMatching(m)} />
            ))}
          </div>
        )}
      </div>
      <BottomNav />
    </div>
  );
}

function MatchingCard({ m, t, lang, onOpen }) {
  const score = m.totalScore ?? 0;
  const strong = m.strongMatch ?? score >= 90;
  const levelColor = strong ? T.gold : T.cyan;
  const levelBg = strong ? T.goldL : T.cyanL;
  const levelLabel = strong ? t("matchings.level_fort") : t("matchings.level_probable");

  return (
    <div style={{ background:T.white, borderRadius:"16px",
      border:`1.5px solid ${T.border}`, borderLeft:`4px solid ${levelColor}`,
      padding:"20px", boxShadow:"0 2px 10px rgba(10,31,61,0.04)" }}>

      {/* En-tete : score + niveau + statut */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        marginBottom:"16px", flexWrap:"wrap", gap:"10px" }}>
        <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
          <div style={{ width:"46px", height:"46px", borderRadius:"14px",
            background:levelBg, display:"flex", alignItems:"center",
            justifyContent:"center", fontSize:"20px", flexShrink:0 }}>🎯</div>
          <div>
            <div style={{ fontWeight:800, fontSize:"18px", color:T.night, lineHeight:1.1 }}>
              {score}<span style={{ fontSize:"12px", fontWeight:600, color:T.sub }}>/100</span>
            </div>
            <span style={{ fontSize:"10px", fontWeight:700, color:levelColor,
              background:levelBg, padding:"2px 8px", borderRadius:"20px",
              textTransform:"uppercase", letterSpacing:"0.5px" }}>
              {levelLabel}
            </span>
          </div>
        </div>
        <span style={{ fontSize:"11px", fontWeight:700, padding:"4px 10px",
          borderRadius:"20px", background:"#F1F5F9", color:T.sub }}>
          {t(`matchings.status.${m.status}`, { defaultValue: m.status })}
        </span>
      </div>

      {/* Barre de score */}
      <div style={{ height:"6px", borderRadius:"6px", background:"#F1F5F9",
        marginBottom:"18px", overflow:"hidden" }}>
        <div style={{ height:"100%", width:`${Math.min(score,100)}%`,
          background:levelColor, borderRadius:"6px" }} />
      </div>

      {/* Objets */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px" }}>
        <ObjectSummary
          label={t("matchings.lost_object")}
          badgeColor={T.royal} badgeBg="#EFF6FF"
          obj={m.lostObject}
          location={m.lostObject?.location}
          date={formatDate(m.lostObject?.date, lang)}
          t={t}
        />
        <ObjectSummary
          label={t("matchings.found_object")}
          badgeColor={T.gold} badgeBg={T.goldL}
          obj={m.foundObject}
          location={m.foundObject?.location}
          date={formatDate(m.foundObject?.date, lang)}
          t={t}
        />
      </div>

      <button onClick={onOpen}
        style={{ marginTop:"18px", width:"100%", background:T.night, color:T.white,
          border:"none", borderRadius:"10px", padding:"12px", fontWeight:700,
          fontSize:"13px", cursor:"pointer" }}>
        {t("matchings.view_detail")}
      </button>
    </div>
  );
}

function ObjectSummary({ label, badgeColor, badgeBg, obj, location, date, t }) {
  if (!obj) return null;
  return (
    <div style={{ background:"#F8FAFC", borderRadius:"12px", padding:"12px" }}>
      <span style={{ fontSize:"10px", fontWeight:700, color:badgeColor,
        background:badgeBg, padding:"2px 8px", borderRadius:"20px",
        textTransform:"uppercase", letterSpacing:"0.5px" }}>
        {label}
      </span>
      <div style={{ fontWeight:700, fontSize:"14px", color:"#1E293B",
        margin:"8px 0 2px" }}>
        {obj.name || t(`categories.${obj.category}`, { defaultValue: obj.category })}
      </div>
      <div style={{ fontSize:"12px", color:"#64748B", lineHeight:"1.7" }}>
        {t(`categories.${obj.category}`, { defaultValue: obj.category })}
        {obj.mainColor && <> · {t(`colors.${(obj.mainColor||"").toLowerCase()}`, { defaultValue: obj.mainColor })}</>}
        <br/>
        📍 {location || t("matching.location_after_mutual")}<br/>
        📅 {date}
      </div>
    </div>
  );
}
